// The rack — Predict Six's six-ball accuracy gauge (web side).
//
// LOCKSTEP: mirrors ios/PredictSix/Models/ShareStory.swift (RackMath +
// StorySelector). Change one → change both. Spec:
// docs/share-artifact-proposal.md §4 (v4, 2026-07-02).
//
// Six circles; round(correct/resolved × 6) render gold, the rest hollow.
// A GAUGE, never a per-question ledger. Gold is the only scoring color.

/// No rack under this many resolutions in the period (1-for-1 showing
/// six gold reads as cheap).
export const RACK_MINIMUM_SAMPLE = 3;

/// Rack fill required before a record is share-worthy (aspirational
/// scarcity — offers only fire on strong states).
export const RACK_MIN_SHARE_FILL = 4;

/// A correct call counts as an upset when fewer than this % of the crowd
/// was on the user's side (matches the reveal flavor-text threshold).
export const UPSET_CROWD_THRESHOLD = 40;

export function rackFill(correct, resolved) {
  if (!resolved || resolved <= 0) return 0;
  const raw = Math.round((correct / resolved) * 6);
  return Math.max(0, Math.min(6, raw));
}

/// Emoji form for share text: 🟡 filled · ⚪ unfilled.
export function rackEmoji(filled) {
  const f = Math.max(0, Math.min(6, filled));
  return '🟡'.repeat(f) + '⚪'.repeat(6 - f);
}

/// Pick tonight's ONE share story by newsworthiness, or null (quiet
/// night). Mirrors StorySelector.select on iOS (ShareStory.swift) —
/// including link anchors: receipts anchor their own question; records
/// anchor tonight's most newsworthy correct call; sweat anchors the next
/// resolving open call ("next leg").
///
/// tonight: decodeAnswer rows (data/results.js): { questionId, text,
///   answer, correctAnswer, isCorrect, isVoided, communityYesPercent }.
/// nextLeg: { id, text } | null.
export function selectStory({
  tonight = [],
  weekCorrect = 0,
  weekResolved = 0,
  openCalls = 0,
  nextLeg = null,
  h2hRivalry = null,
}) {
  const scored = tonight.filter(
    (q) => !q.isVoided && typeof q.correctAnswer === 'boolean' && typeof q.answer === 'boolean'
  );
  const correct = scored.filter((q) => q.isCorrect);
  const resolved = scored.length;

  // Most newsworthy correct call = lowest crowd agreement with the
  // user's side (communityNoPercent derives as 100 − yes).
  const ranked = correct
    .map((q) => {
      const yes = typeof q.communityYesPercent === 'number' ? q.communityYesPercent : 50;
      const agree = q.answer ? yes : 100 - yes;
      return { q, agree };
    })
    .sort((a, b) => a.agree - b.agree);
  const anchor = ranked[0]?.q?.questionId ?? null;

  // 1. Perfect night.
  if (resolved >= RACK_MINIMUM_SAMPLE && correct.length === resolved) {
    return { kind: 'receipt_perfect', correct: resolved, anchorQuestionId: anchor };
  }

  // 2. Upset receipt — the correct call the crowd disagreed with hardest.
  if (ranked.length > 0 && ranked[0].agree < UPSET_CROWD_THRESHOLD) {
    const { q, agree } = ranked[0];
    return {
      kind: 'receipt_upset',
      questionId: q.questionId,
      questionText: q.text,
      crowdAgainstPercent: Math.round(100 - agree),
      anchorQuestionId: q.questionId,
    };
  }

  // 2.5 Rivalry win (C2) — beating a NAMED friend on tonight's reveal outranks
  //     a plain night record. Only on a WIN; the caller supplies the tally.
  if (h2hRivalry && h2hRivalry.myCorrect > h2hRivalry.theirCorrect) {
    return {
      kind: 'rivalry_result',
      opponentName: h2hRivalry.opponentName,
      myCorrect: h2hRivalry.myCorrect,
      theirCorrect: h2hRivalry.theirCorrect,
      anchorQuestionId: h2hRivalry.anchorQuestionId ?? anchor,
    };
  }

  // 3. Night record (rack ≥ 4 on ≥ min sample).
  if (resolved >= RACK_MINIMUM_SAMPLE && rackFill(correct.length, resolved) >= RACK_MIN_SHARE_FILL) {
    return { kind: 'receipt_night', correct: correct.length, resolved, anchorQuestionId: anchor };
  }

  // 4. Sweat — strong week + enough live calls.
  if (
    weekResolved >= RACK_MINIMUM_SAMPLE &&
    rackFill(weekCorrect, weekResolved) >= RACK_MIN_SHARE_FILL &&
    openCalls >= 5
  ) {
    return {
      kind: 'sweat',
      weekCorrect,
      weekResolved,
      openCalls,
      anchorQuestionId: nextLeg?.id ?? null,
      legText: nextLeg?.text ?? null,
    };
  }

  return null;
}
